import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Bell } from 'lucide-react';
import {
  getUserPreferences,
  updateUserPreferences,
} from '../../../api/General/NotificationPreference';
import type {
  NotificationPreferenceView,
  NotificationPreferenceItemInput,
} from '../../../api/General/NotificationPreference';
import NotificationPreferenceMatrix from '../../../components/notifications/NotificationPreferenceMatrix';
import DevicePermissionsPanel from '../../../components/permissions/DevicePermissionsPanel';
import { Spinner } from '../../../components/ui/Spinner';
import Button from '../../../components/ui/Button';
import { PageLayout } from '../../../components/ui/PageLayout';
import AppIcon from '../../../components/AppIcon';
import { enableWebPush } from '../../../utils/webPush';
import type { RootState } from '../../../store/redux/store/store';

const UserNotificationPreferences: React.FC = () => {
  const { t } = useTranslation();
  const userRole = useSelector((state: RootState) => state.user.role);
  const [preferences, setPreferences] = useState<NotificationPreferenceView[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>(
    'idle'
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingChangesRef = useRef<NotificationPreferenceItemInput[]>([]);

  useEffect(() => {
    fetchPreferences();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const result = await getUserPreferences();
      if (result.status === 200 && result.payload) {
        setPreferences(result.payload);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = useCallback(
    (
      notificationTypeId: number,
      channel: 'email' | 'push' | 'inApp',
      value: boolean
    ) => {
      // Update local state immediately for responsiveness
      setPreferences((prev) =>
        prev.map((p) => {
          if (p.notificationTypeId !== notificationTypeId) return p;
          return {
            ...p,
            [channel]: { ...p[channel], enabled: value, source: 'user' as const },
          };
        })
      );

      // Build preference input - merge with any pending change for this type
      const channelMap = {
        email: 'emailEnabled',
        push: 'pushEnabled',
        inApp: 'inAppEnabled',
      } as const;

      const existing = pendingChangesRef.current.find(
        (c) => c.notificationTypeId === notificationTypeId
      );

      if (existing) {
        existing[channelMap[channel]] = value;
      } else {
        const currentPref = preferences.find(
          (p) => p.notificationTypeId === notificationTypeId
        );
        pendingChangesRef.current.push({
          notificationTypeId,
          emailEnabled:
            channel === 'email'
              ? value
              : currentPref?.email.source === 'user'
                ? currentPref.email.enabled
                : null,
          pushEnabled:
            channel === 'push'
              ? value
              : currentPref?.push.source === 'user'
                ? currentPref.push.enabled
                : null,
          inAppEnabled:
            channel === 'inApp'
              ? value
              : currentPref?.inApp.source === 'user'
                ? currentPref.inApp.enabled
                : null,
        });
      }

      // Debounce the API call
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setSaveStatus('saving');

      debounceRef.current = setTimeout(async () => {
        try {
          const changes = [...pendingChangesRef.current];
          pendingChangesRef.current = [];
          await updateUserPreferences(changes);
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (error) {
          console.error('Failed to save preferences:', error);
          setSaveStatus('idle');
        }
      }, 500);
    },
    [preferences]
  );

  const handleResetAll = async () => {
    // Set all to null to inherit from company defaults
    const resetItems: NotificationPreferenceItemInput[] = preferences.map(
      (p) => ({
        notificationTypeId: p.notificationTypeId,
        emailEnabled: null,
        pushEnabled: null,
        inAppEnabled: null,
      })
    );

    setSaveStatus('saving');
    try {
      await updateUserPreferences(resetItems);
      await fetchPreferences();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to reset preferences:', error);
      setSaveStatus('idle');
    }
  };

  return (
    <PageLayout
      title={t('Notification Preferences')}
      description={t('Your administrator may have disabled some notifications')}
      icon={Bell}
      accentColor="slate"
      headerActions={(
        <div className="flex items-center gap-3 flex-wrap">
          {saveStatus === 'saving' && (
            <span className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Spinner size="sm" />
              {t('Saving')}...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1.5">
              <AppIcon name="Check" size={16} />
              {t('Saved')}
            </span>
          )}
          <Button
            variant="outline"
            iconName="RotateCcw"
            onClick={handleResetAll}
            disabled={loading}
          >
            {t('Reset All to Defaults')}
          </Button>
        </div>
      )}
    >
      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : (
        <NotificationPreferenceMatrix
          mode="user"
          preferences={preferences}
          onToggle={handleToggle}
          userRole={userRole}
        />
      )}

      {/* Device permissions — browser-level access this site can request. Independent
          of the preference matrix above (which controls what we send), so it renders
          regardless of the preferences fetch. */}
      <section className="mt-8">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-foreground">
            {t('Device permissions')}
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('Manage what this site can access on your device.')}
          </p>
        </div>
        <DevicePermissionsPanel onEnableNotifications={enableWebPush} />
      </section>
    </PageLayout>
  );
};

export default UserNotificationPreferences;
